import React, { useCallback, useMemo, useState } from "react";
import { StyleSheet } from "react-native";
import { MissionDeduction, Repetition, RepetitionType } from "../../__generated__/graphql";
import { useTheme } from "../../hooks/use-theme";
import { BodyMediumText } from "../FormattedText";
import { useI18n } from "../../hooks/i18n-hooks";
import VStack from "../../ui_atoms/VStack";
import TextField from "../TextField";
import SelectField from "../SelectField";
import ListItem from "../../ui_atoms/ListItem";
import HStack from "../../ui_atoms/HStack";
import Gap from "../../ui_atoms/Gap";
import { useChainInputFocus } from "../../hooks/use-chain-input-focus";
import { useSetAlert } from "../../hooks/use-set-alert";
import MoneyField from "../MoneyField";

export type MissionDeductionResult = Partial<MissionDeduction>;

export const validateDeductionData = (data: MissionDeduction) => {
    if (!data.description || data.description.length < 3) {
        return false;
    }
    return (data.amount || 0) > 0;
};

type MissionDeductionElementProps = {
    deduction: MissionDeduction;
    setDeduction: (deduction: MissionDeductionResult) => void;
    onRemove: () => void;
    repetition?: Repetition;
};

const MissionDeductionElement: React.FC<MissionDeductionElementProps> = ({
    deduction,
    setDeduction,
    onRemove,
    repetition,
}) => {
    const styles = useStyles();
    const { colors, gridSize } = useTheme();
    const i18n = useI18n();
    const [isValueFocused, setIsValueFocused] = useState(false);

    const amountValue = deduction.amount;
    const descriptionValue = deduction.description;

    const buildRepetitionOption = useCallback(
        (repetitionOption: RepetitionType) => {
            let label = i18n.t(`fees.repetition.${repetitionOption}`);
            if (repetition === Repetition.None && repetitionOption === RepetitionType.WorkedMonth) {
                label = i18n.t("fees.repetition.total");
            }
            return {
                label,
                value: repetitionOption,
            };
        },
        [i18n, repetition],
    );

    const repetitionsOptions = useMemo(() => {
        return [RepetitionType.WorkedMonth, RepetitionType.WorkedDay].map(buildRepetitionOption);
    }, [buildRepetitionOption]);

    const setAmount = useCallback(
        (amount?: number) => {
            if (amount === undefined) {
                setDeduction({
                    ...deduction,
                    amount: undefined,
                });
            } else {
                setDeduction({
                    ...deduction,
                    amount: Math.round(amount * 100),
                });
            }
        },
        [deduction, setDeduction],
    );

    const setDescription = useCallback(
        (newValue: string) => {
            setDeduction({
                ...deduction,
                description: newValue,
            });
        },
        [deduction, setDeduction],
    );

    const { chainInputFocus } = useChainInputFocus();

    return (
        <VStack gap={0.5}>
            <HStack>
                <MoneyField
                    {...chainInputFocus("0")}
                    value={amountValue ? amountValue / 100 : undefined}
                    onChange={setAmount}
                    error={!amountValue && !isValueFocused}
                    onFocus={() => {
                        setIsValueFocused(true);
                    }}
                    onBlur={() => {
                        setIsValueFocused(false);
                    }}
                    showErrorText={false}
                    selectTextOnFocus
                    style={styles.amount}
                    autoFocus={!amountValue}
                    leftColor={colors.greyAccessible}
                />
                <Gap gap={0.5} />
                <SelectField
                    value={deduction.repetition}
                    data={repetitionsOptions}
                    onSelect={(newRepetition) => {
                        setDeduction({
                            ...deduction,
                            repetition: newRepetition.value,
                        });
                    }}
                    style={styles.repetition}
                />
            </HStack>
            <TextField
                {...chainInputFocus("1")}
                label={i18n.t("screens.user.create_mission_deductions.description_field")}
                value={descriptionValue}
                onChangeText={setDescription}
                maxLength={200}
                error={!!descriptionValue && descriptionValue.length < 3}
                showErrorText={false}
                multiline
            />
            <ListItem
                labels={[
                    {
                        text: i18n.t("screens.user.create_mission_deductions.remove_deduction"),
                        mode: "redCta",
                    },
                ]}
                onPress={onRemove}
                rightIcon="trash"
                rightActionMode="redCta"
                style={{ marginHorizontal: -gridSize, paddingHorizontal: gridSize }}
            />
        </VStack>
    );
};

type MissionDeductionProps = {
    setDeductions: (fees: Array<MissionDeductionResult>) => void;
    deductions: Array<MissionDeduction>;
    repetition?: Repetition;
};

const MissionDeductions: React.FC<MissionDeductionProps> = ({
    setDeductions,
    deductions,
    repetition,
}) => {
    const { setAlert } = useSetAlert();
    const { gridSize } = useTheme();
    const i18n = useI18n();
    const setDeduction = useCallback(
        (idx: number, deduction: MissionDeductionResult) => {
            const newDeductions = [...deductions] as Array<MissionDeductionResult>;
            newDeductions[idx] = deduction;
            setDeductions(newDeductions);
        },
        [setDeductions, deductions],
    );

    const addDeduction = useCallback(() => {
        const newDeductions = [...deductions] as Array<MissionDeductionResult>;
        newDeductions.push({
            description: "",
            amount: undefined,
            repetition: RepetitionType.WorkedDay,
        });
        setDeductions(newDeductions);
    }, [setDeductions, deductions]);

    const removeDeduction = useCallback(
        (idx: number) => {
            setAlert({
                title: i18n.t("screens.user.create_mission_deductions.remove_title"),
                message: i18n.t("screens.user.create_mission_deductions.remove_info"),
                buttons: [
                    {
                        text: i18n.t("cancel"),
                        style: "cancel",
                    },
                    {
                        text: i18n.t("screens.user.create_mission_deductions.do_remove"),
                        style: "destructive",
                        onPress: () => {
                            const newDeductions = [...deductions] as Array<MissionDeductionResult>;
                            newDeductions.splice(idx, 1);
                            setDeductions(newDeductions);
                        },
                    },
                ],
                options: { cancelable: true },
            });
        },
        [setAlert, i18n, deductions, setDeductions],
    );

    return (
        <VStack gap={1}>
            <BodyMediumText>{i18n.t("screens.user.create_mission_deductions.info")}</BodyMediumText>
            {deductions.map((deduction, idx) => {
                return (
                    <MissionDeductionElement
                        key={`${idx}`}
                        deduction={deduction}
                        setDeduction={(d) => setDeduction(idx, d)}
                        onRemove={() => removeDeduction(idx)}
                        repetition={repetition}
                    />
                );
            })}
            <ListItem
                labels={[
                    {
                        text: i18n.t("screens.user.create_mission_deductions.add_deduction"),
                        mode: "cta",
                    },
                ]}
                onPress={addDeduction}
                rightIcon="add-large"
                rightActionMode="action_simple"
                style={{ paddingHorizontal: -(gridSize / 2), borderBottomWidth: 0 }}
            />
        </VStack>
    );
};

const useStyles = () => {
    const { colors, fonts, gridSize } = useTheme();
    return useMemo(
        () =>
            StyleSheet.create({
                container: {
                    flexDirection: "column",
                    alignItems: "stretch",
                    justifyContent: "flex-start",
                },
                text: {
                    ...fonts.bodyMedium,
                    color: colors.text,

                    padding: gridSize,
                },
                amount: {
                    flexGrow: 0,
                    width: 115,
                },
                repetition: {
                    flex: 1,
                },
            }),
        [colors.text, fonts.bodyMedium, gridSize],
    );
};

export default MissionDeductions;
